import React, { useState, useEffect, useRef } from 'react';
import productAPI from '../../services/productapi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faPlus, faImages } from '@fortawesome/free-solid-svg-icons';
import ItemImagesModal from './components/ItemImagesModal';
import './ProductItemForm.css';
import ProductItemEanModal from "./ProductItemEanModal";

const ProductItemForm = ({ productId, productItems = [], onItemSave, toast }) => {
    const [items, setItems] = useState([]);
    const savedItems = useRef([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);

    // ✅ modal state
    const [showImgModal, setShowImgModal] = useState(false);
    const [activeItemIndex, setActiveItemIndex] = useState(null);
    const [deletedItemIds, setDeletedItemIds] = useState([]);
    const [showEanModal, setShowEanModal] = useState(false);
    const [activeEanItem, setActiveEanItem] = useState(null);

    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const { data } = await productAPI.getProductUnits(100, 0);
                setUnits(data.result || []);
            } catch (error) {
                console.error('Error fetching units:', error);
                toast?.error('Error fetching units');
            }
        };
        fetchUnits();
    }, [toast]);

    // local id helper for images (needed for modal selection)
    const ensureImageId = (img) => {
        if (img?.id) return img.id;
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };


    useEffect(() => {
        const toImageUrl = (path) => {
            if (!path) return "";
            if (/^https?:\/\//i.test(path)) return path;

            const base = (process.env.REACT_APP_IMAGE_URL || "").replace(/\/+$/, "");
            const clean = String(path).replace(/^\/+/, ""); // remove starting /
            return `${base}/uploads/${clean}`;
        };

        const mappedItems = (productItems || []).map((item) => {

            // ✅ Prefer itemImages from API
            const apiImgs = Array.isArray(item.itemImages) ? item.itemImages : [];

            let images = [];

            if (apiImgs.length > 0) {
                images = apiImgs
                    .filter((x) => Number(x?.status ?? 1) === 1 || x?.status === undefined) // safe
                    .sort((a, b) => {
                        // primary first, then sortOrder
                        const ap = Number(a.isPrimary || 0);
                        const bp = Number(b.isPrimary || 0);
                        if (bp !== ap) return bp - ap;
                        return Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
                    })
                    .map((x) => ({
                        id: String(x.id),
                        url: toImageUrl(x.imageUrl),
                        file: null,
                        existing: true,
                    }));
            } else {
                // fallback for old products
                const img0 = toImageUrl(item.image);
                const img1 = toImageUrl(item.image1);
                if (img0) images.push({ id: ensureImageId({}), url: img0, file: null, existing: true });
                if (img1) images.push({ id: ensureImageId({}), url: img1, file: null, existing: true });
            }

            const selection = {
                primaryId: images?.[0]?.id || null,
                secondaryId: images?.[1]?.id || null,
            };
            console.log("IMG FULL:", toImageUrl(apiImgs?.[0]?.imageUrl));
            return {
                item_id: item.productItemId,
                item_branch: item.branchID,
                item_spec: item.unit || '',
                item_unit: item.unitName || '',
                item_ref_code: item.refCode || '',
                item_mrp: parseFloat(item.mrp) || 0,
                item_markup: parseFloat(item.markup) || 0,
                item_sell_price: parseFloat(item.sellPrice) || 0,
                item_gracecoins: item.graceCoins || 0,

                //  row thumbnails from images array
                item_image: images?.[0]?.url || '',
                item_image1: images?.[1]?.url || '',

                images,
                imageSelection: selection,

                item_lowstock_level: item.lowStockLevel || 0,
                veg_flag: Number(item.vegFlag || 0),
                stock_flag: item.stockFlag || 1,
                item_status: item.status || 1,
                removedImageIds: [],
            };
        });

        savedItems.current = mappedItems;
        setItems(mappedItems);
    }, [productItems]);
    // console.log(productItems[0].itemImages, "Image Uploads");

    const validateImageFile = (file) => {
        const fileType = file.type;
        const fileSizeMB = file.size / 1024 / 1024;

        const okType =
            fileType === 'image/jpeg' ||
            fileType === 'image/png' ||
            fileType === 'image/webp';

        if (!okType) return 'Only jpeg/png/webp allowed';
        if (fileSizeMB > 2) return 'Image must be under 2MB';
        return null;
    };

    // reorder images to keep concept: images[0]=primary, images[1]=secondary
    const reorderBySelection = (images = [], selection = {}) => {
        const { primaryId, secondaryId } = selection || {};
        const arr = [...images];

        if (!arr.length) return [];

        const findIdx = (id) => arr.findIndex((x) => x.id === id);

        // move primary to index 0
        if (primaryId) {
            const pIdx = findIdx(primaryId);
            if (pIdx > 0) {
                const [picked] = arr.splice(pIdx, 1);
                arr.unshift(picked);
            }
        }

        // move secondary to index 1 (but not same as primary)
        if (secondaryId && secondaryId !== primaryId) {
            const sIdx = findIdx(secondaryId);
            if (sIdx >= 0) {
                const [picked] = arr.splice(sIdx, 1);
                if (arr.length === 0) arr.push(picked);
                else arr.splice(1, 0, picked);
            }
        }

        // if secondary accidentally equals primary after reorder, clear it
        return arr;
    };

    const openImagesModal = (index) => {
        setActiveItemIndex(index);
        setShowImgModal(true);
    };

    const closeImagesModal = () => {
        setShowImgModal(false);
        setActiveItemIndex(null);
    };

    // ✅ when modal saves images
    const handleModalChangeImages = (nextModalImages) => {
        if (activeItemIndex === null) return;

        setItems((prev) => {
            const updated = [...prev];
            const item = { ...updated[activeItemIndex] };
            updated[activeItemIndex] = item;



            // modal gives {id,file,previewUrl} format; convert to our format {id,url,file,existing}
            const converted = (nextModalImages || []).map((m) => ({
                id: m.id || ensureImageId(m),
                url: m.previewUrl,  // modal uses previewUrl
                file: m.file || null,
                existing: m.file ? false : true, // if file present => new
            }));
            const isDbId = (id) => /^\d+$/.test(String(id));
            const prevExistingIds = (item.images || [])
                .filter(x => x.existing && isDbId(x.id)) // db ids
                .map(x => String(x.id));

            const nextExistingIds = (converted || [])
                .filter(x => x.existing && isDbId(x.id))
                .map(x => String(x.id));

            const removed = prevExistingIds.filter(id => !nextExistingIds.includes(id));

            item.removedImageIds = Array.from(new Set([...(item.removedImageIds || []), ...removed]))

            item.images = converted;

            // update selection if empty
            const sel = item.imageSelection || { primaryId: null, secondaryId: null };
            if (!sel.primaryId && converted.length > 0) sel.primaryId = converted[0].id;
            if (!sel.secondaryId && converted.length > 1) sel.secondaryId = converted[1].id;

            item.imageSelection = sel;

            // keep concept (order) using selection
            const reordered = reorderBySelection(item.images, item.imageSelection);
            item.images = reordered;

            // update backward preview fields
            item.item_image = reordered?.[0]?.url || '';
            item.item_image1 = reordered?.[1]?.url || '';

            // also reset selection to match reordered order
            item.imageSelection = {
                primaryId: reordered?.[0]?.id || null,
                secondaryId: reordered?.[1]?.id || null,
            };

            return updated;
        });
    };

    // ✅ when modal saves selection (primary/secondary)
    const handleModalChangeSelection = (nextSel) => {
        if (activeItemIndex === null) return;

        setItems((prev) => {
            const updated = [...prev];
            const item = { ...updated[activeItemIndex] };
            updated[activeItemIndex] = item;

            item.imageSelection = nextSel || { primaryId: null, secondaryId: null };

            // reorder and update previews
            const reordered = reorderBySelection(item.images || [], item.imageSelection);
            item.images = reordered;

            item.item_image = reordered?.[0]?.url || '';
            item.item_image1 = reordered?.[1]?.url || '';

            // keep selection aligned with order (concept stable)
            item.imageSelection = {
                primaryId: reordered?.[0]?.id || null,
                secondaryId: reordered?.[1]?.id || null,
            };

            return updated;
        });
    };

    // ✅ Add files from modal (we validate here by filtering after modal adds)
    // NOTE: Your modal currently adds files internally. So validation is best done at Save time.
    // But we still validate during Save Items and block if invalid.
    const validateAllSelectedFiles = () => {
        for (const item of items) {
            const imgs = item.images || [];
            for (const img of imgs) {
                if (img.file) {
                    const err = validateImageFile(img.file);
                    if (err) return err;
                }
            }
        }
        return null;
    };

    const handleAddItem = (e) => {
        e.preventDefault();

        const newItem = {
            item_id: null,
            item_branch: null,
            item_spec: '',
            item_unit: '',
            item_ref_code: '',
            item_mrp: 0,
            item_markup: 0,
            item_sell_price: 0,
            item_gracecoins: 0,

            item_image: '',
            item_image1: '',

            images: [],
            imageSelection: { primaryId: null, secondaryId: null },
            removedImageIds: [],

            item_lowstock_level: 0,
            veg_flag: 0,
            stock_flag: 1,
            item_status: 1,
        };

        setItems((prev) => [...prev, newItem]);
    };

    const handleItemChange = (index, field, value) => {
        setItems((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleVegFlagChange = (index, enabled) => {
        const item = items[index];
        const spec = Number(item.item_spec);
        if (!Number.isFinite(spec) || spec <= 0) {
            toast?.error('Enter a valid positive item spec before changing Veg Flag');
            return;
        }
        // Match the existing GoFrugal conversion: 1 = whole kg/l; other specs are gm/ml.
        const factor = spec === 1 ? 1 : spec / 1000;
        const ratio = enabled ? factor : 1 / factor;
        setItems(prev => prev.map((row, i) => i === index ? {
            ...row,
            veg_flag: enabled ? 1 : 0,
            item_mrp: Number((Number(row.item_mrp) * ratio).toFixed(2)),
            item_sell_price: Number((Number(row.item_sell_price) * ratio).toFixed(2)),
        } : row));
    };

    const handleItemDelete = (index) => {
        setItems((prev) => {
            const item = prev[index];
            if (item?.item_id) {
                setDeletedItemIds((ids) => [...ids, item.item_id]);
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleSaveItems = async () => {
        const fileErr = validateAllSelectedFiles();
        if (fileErr) {
            toast?.error(fileErr);
            return;
        }

        setLoading(true);

        const formData = new FormData();
        formData.append('productId', productId);
        // formData.append('deleteMissingItems', '0');
        formData.append('deleteMissingItems', '1');
        items.forEach((item, index) => {
            // formData.append(`items[${index}][itemId]`, item.item_id);
            formData.append(`items[${index}][itemId]`, item.item_id ?? '');
            formData.append(`items[${index}][productID]`, productId);
            formData.append(`items[${index}][unit]`, item.item_unit);
            formData.append(`items[${index}][spec]`, item.item_spec);
            formData.append(`items[${index}][refCode]`, item.item_ref_code);
            formData.append(`items[${index}][pack]`, 1);
            formData.append(`items[${index}][branchID]`, item.item_branch ?? '');
            formData.append(`items[${index}][mrp]`, item.item_mrp ?? 0);
            formData.append(`items[${index}][markup]`, item.item_markup ?? 0);
            formData.append(`items[${index}][sellPrice]`, item.item_sell_price ?? 0);
            formData.append(`items[${index}][graceCoins]`, item.item_gracecoins ?? 0);
            formData.append(`items[${index}][lowStockLevel]`, item.item_lowstock_level ?? 0);
            formData.append(`items[${index}][trackStock]`, 1);
            formData.append(`items[${index}][vegFlag]`, item.veg_flag ?? 0);
            formData.append(`items[${index}][stockFlag]`, item.stock_flag ?? 1);
            formData.append(`items[${index}][createdBy]`, 0);
            formData.append(`items[${index}][editedBy]`, 0);
            formData.append(`items[${index}][editIp]`, '1781759801');
            formData.append(`items[${index}][status]`, item.item_status ?? 1);
            formData.append(`items[${index}][updatedAt]`, new Date().toISOString());
            formData.append(`items[${index}][lastUpdatedAt]`, new Date().toISOString());
            formData.append(`items[${index}][syncDate]`, new Date().toISOString());
            formData.append(`items[${index}][item_pro_id]`, productId);
            formData.append(`items[${index}][primaryId]`, item.imageSelection?.primaryId || '');
            formData.append(`items[${index}][secondaryId]`, item.imageSelection?.secondaryId || '');
            const primaryIndexRaw = (item.images || []).findIndex(x => x.id === item.imageSelection?.primaryId);
            const primaryIndex = primaryIndexRaw >= 0 ? primaryIndexRaw : 0;
            formData.append(`items[${index}][primaryIndex]`, String(primaryIndex));
            // ✅ keep your existing concept: only append NEW files (multer array)
            const images = item.images || [];
            images.forEach((img) => {
                if (img.file) {
                    formData.append(`items[${index}][images]`, img.file);
                }
            });

            // optional for backend future usage (won't break current flow)
            formData.append(`items[${index}][primaryPreview]`, item.item_image || '');
            formData.append(`items[${index}][secondaryPreview]`, item.item_image1 || '');

            formData.append(
                `items[${index}][removedImageIds]`,
                JSON.stringify(item.removedImageIds || [])
            );
        });
        formData.append('deletedItemIds', JSON.stringify(deletedItemIds));

        try {
            await productAPI.updateProductsItems(formData);
            toast?.success('Product item updated successfully!');
            setDeletedItemIds([]);
            await onItemSave?.();
        } catch (error) {
            console.error('Error saving items:', error);
            toast?.error('Error saving items');
        } finally {
            setLoading(false);
        }
    };

    const activeItem = activeItemIndex !== null ? items[activeItemIndex] : null;

    // convert to modal expected format: {id,file,previewUrl}
    const modalImages = (activeItem?.images || []).map((img) => ({
        id: img.id,
        file: img.file,
        previewUrl: img.url
    }));

    const modalSelection = activeItem?.imageSelection || { primaryId: null, secondaryId: null };

    return (
        <section className="product-items-editor">
            <header className="pie-heading">
                <div><h3>Product Items</h3><p>Manage variants, pricing and inventory settings.</p></div>
                <button className="pie-button pie-primary" type="button" onClick={handleAddItem} disabled={loading}>
                    <FontAwesomeIcon icon={faPlus} /> Add New Item
                </button>
            </header>
            <div className="pie-info"><span aria-hidden="true">ⓘ</span> Veg Flag applies weight-based POS pricing. Review prices before saving.</div>
            <div className="pie-variants">
                {items.length === 0 && <div className="pie-empty">No variants yet. Add a new item to get started.</div>}
                {items.map((item, index) => (
                    <article className="pie-card" key={item.item_id || `new-${index}`}>
                        <header className="pie-card-heading">
                            <h4>Variant {String(index + 1).padStart(2, '0')}</h4>
                            <div className="pie-card-actions">
                                <select aria-label={`Variant ${index + 1} status`} value={item.item_status} disabled={loading}
                                    onChange={e => handleItemChange(index, 'item_status', e.target.value)}>
                                    <option value={1}>Active</option><option value={2}>Inactive</option>
                                </select>
                                <button className="pie-delete" type="button" disabled={loading} onClick={() => handleItemDelete(index)}
                                    aria-label={`Remove variant ${index + 1}`} title="Remove variant"><FontAwesomeIcon icon={faTrashAlt} /></button>
                            </div>
                        </header>
                        <div className="pie-card-body">
                            <div className="pie-images">
                                <div className="pie-thumbnail">
                                    {item.item_image ? <img src={item.item_image} alt={`Variant ${index + 1} primary`} /> : <span>No image</span>}
                                </div>
                                <button className="pie-image-link" type="button" disabled={loading} onClick={() => openImagesModal(index)}>
                                    <FontAwesomeIcon icon={faImages} /> Manage Images
                                </button>
                            </div>
                            <div className="pie-fields">
                                <label>Spec<input type="number" min="0" step="any" value={item.item_spec} disabled={loading}
                                    onChange={e => handleItemChange(index, 'item_spec', e.target.value)} /></label>
                                <label>Unit<select value={item.item_unit} disabled={loading} onChange={e => handleItemChange(index, 'item_unit', e.target.value)}>
                                    <option value="">Select Unit</option>
                                    {units.map((u, i) => <option key={i} value={u.unitCode}>{u.unitCode}</option>)}
                                </select></label>
                                <label>Reference Code<input type="text" value={item.item_ref_code} disabled={loading}
                                    onChange={e => handleItemChange(index, 'item_ref_code', e.target.value)} /></label>
                                <label>MRP (₹)<input type="number" min="0" step="0.01" value={item.item_mrp} disabled={loading}
                                    onChange={e => handleItemChange(index, 'item_mrp', e.target.value)} /></label>
                                <label>Markup<input type="number" step="any" value={item.item_markup} disabled={loading}
                                    onChange={e => handleItemChange(index, 'item_markup', e.target.value)} /></label>
                                <label>Sell Price (₹)<input type="number" min="0" step="0.01" value={item.item_sell_price} disabled={loading}
                                    onChange={e => handleItemChange(index, 'item_sell_price', e.target.value)} /></label>
                            </div>
                            <aside className="pie-settings">
                                <h5>Veg Flag</h5>
                                <label className={`pie-toggle ${Number(item.veg_flag) === 1 ? 'is-enabled' : 'is-disabled'}`}>
                                    <input type="checkbox" role="switch" aria-label={`Variant ${index + 1} Veg Flag`} checked={Number(item.veg_flag) === 1}
                                        disabled={loading} onChange={e => handleVegFlagChange(index, e.target.checked)} />
                                    <span className="pie-toggle-track" aria-hidden="true" />
                                    <span>{Number(item.veg_flag) === 1 ? 'Enabled' : 'Disabled'}</span>
                                </label>
                                <p>Weight-based pricing.</p>
                                <button className="pie-button pie-outline" type="button" disabled={loading || !item.item_id}
                                    title={!item.item_id ? 'Save this item before managing EAN codes' : 'Manage EAN codes'}
                                    onClick={() => {
                                        setActiveEanItem({ id: item.item_id, refCode: item.item_ref_code, productName: item.productName || '' });
                                        setShowEanModal(true);
                                    }}>▥ <span>Manage EAN</span></button>
                            </aside>
                        </div>
                    </article>
                ))}
            </div>
            <footer className="pie-footer">
                <button className="pie-button pie-cancel" type="button" disabled={loading} onClick={() => {
                    setItems(savedItems.current.map(row => ({ ...row })));
                    setDeletedItemIds([]);
                    closeImagesModal();
                }}>Cancel</button>
                <button className="pie-button pie-primary" type="button" onClick={handleSaveItems} disabled={loading}>
                    {loading ? 'Saving...' : 'Save Items'}
                </button>
            </footer>

            {/* ✅ Modal */}
            <ItemImagesModal
                show={showImgModal}
                onHide={closeImagesModal}
                title="Manage Item Images"
                images={modalImages}
                onChangeImages={handleModalChangeImages}
                selection={modalSelection}
                onChangeSelection={handleModalChangeSelection}
                maxImages={10}
            />

            <ProductItemEanModal
                show={showEanModal}
                onHide={() => {
                    setShowEanModal(false);
                    setActiveEanItem(null);
                }}
                productItem={activeEanItem}
            />

        </section>
    );
};

export default ProductItemForm;